import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { User, LogIn, Briefcase, ChevronRight, LogOut, Mail, MonitorSmartphone, AlertTriangle, Building, ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AuthModal from "./AuthModal";

const TopBar = () => {
  const navigate = useNavigate();
  
  const [authData, setAuthData] = useState(() => {
    try {
      const candidate = localStorage.getItem("candidate");
      const employer = localStorage.getItem("employer");
      const admin = localStorage.getItem("admin_token");
      if (candidate) return { type: 'candidate', user: JSON.parse(candidate) };
      if (employer) return { type: 'employer', user: JSON.parse(employer) };
      if (admin) return { type: 'admin', user: { name: 'Admin User' } };
      return null;
    } catch (error) {
      return null;
    }
  });

  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState('register');
  const [userRole, setUserRole] = useState('candidate'); 
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  useEffect(() => {
    if (!authData) {
      const timer = setTimeout(() => {
        setIsAuthOpen(true);
      }, 800);
      
      return () => clearTimeout(timer);
    }
  }, [authData]);

  const confirmLogout = () => {
    localStorage.removeItem("candidate");
    localStorage.removeItem("candidateToken");
    localStorage.removeItem("employer");
    localStorage.removeItem("employer_token"); 
    localStorage.removeItem("admin_token");
    setAuthData(null);
    setShowLogoutModal(false);
    window.location.href = '/'; 
  };

  const openAuth = (mode, role = 'candidate') => {
    setAuthMode(mode);
    setUserRole(role);
    setIsAuthOpen(true);
  };

  const handleScrollToContact = (e) => {
    e.preventDefault();
    if (window.location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
      }, 500);
    } else {
      document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <div className="w-full bg-slate-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 border-t-[3px] border-t-[#FED547] shadow-sm text-[13.5px] transition-colors duration-300 relative z-50">
        <div className="max-w-[1200px] mx-auto px-4 flex items-center justify-end gap-5 py-2.5 font-semibold">
          
          <a
            href="/#contact-form"
            onClick={handleScrollToContact}
            className="flex items-center gap-1.5 text-gray-600 hover:text-blue-600 dark:text-gray-300 dark:hover:text-blue-400 transition-colors group"
          >
            <Mail className="w-4 h-4 text-blue-500 group-hover:scale-110 transition-transform" />
            <span className="tracking-wide">Business / Bulk Enquiries</span>
          </a>

          <div className="w-[1.5px] h-4 bg-gray-300 dark:bg-gray-700 rounded-full"></div>

          <button 
            onClick={() => navigate('/admin/login')} 
            className="flex items-center gap-1.5 text-gray-600 hover:text-indigo-600 dark:text-gray-300 dark:hover:text-indigo-400 transition-colors group bg-transparent border-none cursor-pointer font-semibold text-[13.5px]"
          >
            <MonitorSmartphone className="w-4 h-4 text-indigo-500 group-hover:scale-110 transition-transform" />
            <span className="tracking-wide">Kadtech Associate Zone</span>
          </button>

          <div className="w-[1.5px] h-4 bg-gray-300 dark:bg-gray-700 rounded-full"></div>

          <button 
            onClick={() => openAuth('login', 'employer')} 
            className="flex items-center gap-1.5 text-gray-600 hover:text-emerald-600 dark:text-gray-300 dark:hover:text-emerald-400 transition-colors group bg-transparent border-none cursor-pointer font-semibold text-[13.5px]"
          >
            <Briefcase className="w-4 h-4 text-emerald-500 group-hover:scale-110 transition-transform" />
            <span className="tracking-wide">Employer Zone</span>
          </button>

          <div className="w-[1.5px] h-4 bg-gray-300 dark:bg-gray-700 rounded-full"></div>

          <button 
            onClick={() => openAuth('login', 'employer')}
            className="group flex items-center gap-1 bg-[#FED547] text-black px-4 py-1.5 rounded-full text-xs font-bold shadow-[0_0_12px_rgba(254,213,71,0.6)] hover:shadow-[0_0_20px_rgba(254,213,71,0.9)] hover:-translate-y-0.5 transition-all duration-300 border border-[#e6bd35] cursor-pointer"
          >
            POST FREE JOB 
            <ChevronRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
          </button>

          <div className="w-[1.5px] h-4 bg-gray-300 dark:bg-gray-700 rounded-full"></div>

          {authData ? (
            <div className="flex items-center gap-3">
              <button
                onClick={() => { 
                  // 🌟 FIX: URL path corrected to "/candidate/profile"
                  if (authData.type === 'candidate') navigate("/candidate-profile");
                  else if (authData.type === 'employer') navigate("/employer/profile");
                  else if (authData.type === 'admin') navigate("/admin/dashboard");
                }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white border shadow-sm transition-all ${
                  authData.type === 'candidate' 
                    ? 'border-emerald-200 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-800 dark:text-emerald-400' 
                    : authData.type === 'employer'
                    ? 'border-blue-200 text-blue-700 dark:bg-blue-900/30 dark:border-blue-800 dark:text-blue-400'
                    : 'border-amber-200 text-amber-700 dark:bg-amber-900/30 dark:border-amber-800 dark:text-amber-400'
                }`}
              >
                {authData.type === 'candidate' ? <User className="w-4 h-4" /> : authData.type === 'employer' ? <Building className="w-4 h-4" /> : <ShieldCheck className="w-4 h-4" />}
                {authData.user.name || authData.user.company_name || 'Dashboard'}
              </button>

              <button
                onClick={() => setShowLogoutModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-red-500 text-red-600 hover:bg-red-50 hover:text-red-700 dark:border-red-500 dark:text-red-400 dark:hover:bg-red-950/30 transition-all cursor-pointer"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={() => openAuth("login", "candidate")}
                className="flex items-center gap-1.5 px-4 py-1.5 rounded-md bg-white border border-gray-200 text-gray-700 shadow-sm hover:border-gray-400 hover:text-gray-900 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-200 transition-all group cursor-pointer"
              >
                <User className="w-4 h-4 text-gray-500 group-hover:text-gray-800 dark:group-hover:text-white" />
                Login
              </button>

              <button
                onClick={() => openAuth("register", "candidate")}
                className="flex items-center gap-1.5 px-4 py-1.5 rounded-md bg-gray-900 text-white shadow-md hover:bg-gray-800 dark:bg-gray-100 dark:text-gray-900 dark:hover:bg-white transition-all group cursor-pointer"
              >
                <LogIn className="w-4 h-4 text-gray-300 dark:text-gray-700 group-hover:text-white dark:group-hover:text-gray-900" />
                Register
              </button>
            </div>
          )}
        </div>
      </div>

      {showLogoutModal && createPortal(
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[99999] p-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 max-w-sm w-full shadow-2xl border border-gray-100 dark:border-gray-800 text-center animate-in fade-in zoom-in duration-200">
            <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 text-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle size={24} />
            </div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Confirm Logout</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">Are you sure you want to log out of your account?</p>
            <div className="flex gap-3 justify-center">
              <button 
                onClick={() => setShowLogoutModal(false)} 
                className="flex-1 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-200 py-2.5 rounded-xl text-sm font-semibold transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button 
                onClick={confirmLogout} 
                className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2.5 rounded-xl text-sm font-semibold transition-colors shadow-lg shadow-red-500/20 cursor-pointer"
              >
                Yes, Logout
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      <AuthModal
        isOpen={isAuthOpen}
        onClose={() => setIsAuthOpen(false)}
        initialMode={authMode}
        defaultRole={userRole}
      />
    </>
  );
};

export default TopBar;